// src/App.js
import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { NotificationProvider } from './contexts/NotificationContext';
import { VoiceCommandProvider } from "./contexts/VoiceCommandContext";
import { AccessibilityProvider } from "./contexts/AccessibilityContext";
import { Toaster } from 'react-hot-toast';
import { LanguageProvider } from './navigations/homenav';

// Protected Route Components
import { OPDProtectedRoute, AdminProtectedRoute } from "./components/ProtectedRoute";

// Admin Components
import AdminNav from "./navigations/adminnav";
import AssociationDashboard from "./components/AssociationDashboard";
import AssociationMembers from "./components/AssociationMembers";
import AssociationPendingMembers from "./components/AssociationPendingMembers";
import AssociationRejectedMembers from "./components/AssociationRejectedMembers";
import AdminOPDs from "./components/AdminOPDs";
import AdminReports from "./components/AdminReports";
import AdminPlansReports from "./components/AdminPlansReports";
import AdminSettingsForm from "./components/AdminSettingsForm";
import AssociationEmailUpdate from "./components/AssociationEmailUpdate";
import AssociationPasswordUpdate from "./components/AssociationPasswordUpdate";
import AdminMessages from "./components/AdminMessages";
import PerOPDSupportTrackingPage from "./components/PerOPDSupportTrackingPage";
import AssociationRenewals from "./components/AssociationRenewals";

// OPD Components
import OpdsNav from "./navigations/opdsnav";
import OPDDashboard from "./components/OPDDashboard";
import MembersPage from "./components/MembersPage";
import OPDPendingMembers from "./components/OPDPendingMembers";
import OPDRejectedMembers from "./components/OPDRejectedMembers";
import OPDRenewals from "./components/OPDRenewals";
import ChatsPage from "./components/Chats";
import OPDProfile from "./components/OPDProfileForm";
import OPDAccessibility from "./components/OPDAccessibilityPage";
import OPDReports from "./components/OPDReports";
import OPDPlansReports from "./components/OPDPlansReports";
import OPDSupportReceived from "./components/OPDSupportReceived";
import OPDEmailUpdate from "./components/OPDEmailUpdate";
import OPDPasswordUpdate from "./components/OPDPasswordUpdate";
import OPDSupportTrackingPage from "./components/OPDSupportTrackingPage";
import SelfRegisteredMembers from "./components/SelfRegisteredMembers";
import DeletedMembersPage from "./components/DeletedMembersPage";

//Landing page components
import HomePage from "./components/HomePage";
import HomeNav from "./navigations/homenav";
import AboutUsPage from "./components/AboutUsPage";
import ContactUsPage from "./components/ContactUsPage";
import GalleryPage from "./components/GalleryPage";
import ProgramsPage from "./components/ProgramsPage";
import PublicMembersPage from "./components/PublicMembersPage";
import NewsPage from "./components/NewsPage";
import MissionVisionValuesPage from "./components/MissionVisionValuesPage";
import LeadershipPage from "./components/LeadershipPage";
import DisabilityPoliciesResearchPage from "./components/DisabilityPoliciesResearchPage";
import ProgramsAdminPage from "./components/ProgramsAdminPage";
import ScrollToTop from "./components/ScrollToTop";
import ConditionalFloatingForm from "./components/ConditionalFloatingForm";
import FloatingAccessibilityControls from "./components/FloatingAccessibilityControls";
import GalleryAdmin from "./components/GalleryAdmin";
import AdminNews from "./components/AdminNews";
import NewsDetail from "./components/NewsDetail";
import SubmitGallery from "./components/SubmitGallery";
import AdminApprovalGallery from "./components/AdminApprovalGallery";
import AdminApprovalNews from "./components/AdminApprovalNews";
import SubmitNews from "./components/SubmitNews";
import AnnualReportPage from "./components/AnnualReportPage";
import DonationPage from "./components/DonationPage";

// Auth
import Login from "./components/Login";
import ForgotPassword from "./components/ForgotPassword";


// Layout wrapper for main content
const Layout = ({ nav: Nav, children }) => (
  <div className="flex">
    <Nav /> {/* Sidebar Navigation */}
    <main className="flex-1 min-h-screen bg-gray-50 ml-0 md:ml-64 transition-all p-4">
      {children}
    </main>
  </div>
);

const AppWithVoice = () => {
  const navigate = useNavigate();

  // Check if current path is an OPD page
  const isOPDPage = () => {
    const path = window.location.pathname;
    return path.startsWith('/dashboard') ||
           path.startsWith('/members') ||
           path.startsWith('/chats') ||
           path.startsWith('/opdsupport') ||
           path.startsWith('/opdsupporttracking') ||
           path.startsWith('/self-registered-members') ||
           path.startsWith('/opdreports') ||
           path.startsWith('/opdplansreports') ||
           path.startsWith('/opdprofile') ||
           path.startsWith('/opdemailupdate') ||
           path.startsWith('/opdpasswordupdate') ||
           path.startsWith('/opdaccessibility') ||
           path.startsWith('/deleted-members') ||
           path.startsWith('/submit-gallery') ||
           path.startsWith('/submit-news');
  };

  // Check if current path is an Admin page
  const isAdminPage = () => {
    const path = window.location.pathname;
    return path.startsWith('/admindashboard') ||
           path.startsWith('/adminmembers') ||
           path.startsWith('/adminopds') ||
           path.startsWith('/reports') ||
           path.startsWith('/adminplansreports') ||
           path.startsWith('/galleryadmin') ||
           path.startsWith('/adminnews') ||
           path.startsWith('/adminprograms') ||
           path.startsWith('/adminsettings') ||
           path.startsWith('/associationemailupdate') ||
           path.startsWith('/adminpasswordupdate') ||
           path.startsWith('/adminmessages') ||
           path.startsWith('/supportperopd') ||
           path.startsWith('/adminapprovalgallery') ||
           path.startsWith('/adminapprovalnews');
  };

  return (
    <VoiceCommandProvider navigate={navigate}>
      <div className="min-h-screen flex flex-col">
        <Routes>
          {/* Auth */}
          {/* Homenav Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/whoweare" element={<AboutUsPage />} />
          <Route path="/about/mission-vision-values" element={<MissionVisionValuesPage />} />
          <Route path="/about/leadership" element={<LeadershipPage />} />
          <Route path="/annual-report" element={<AnnualReportPage />} />
          <Route path="/donate" element={<DonationPage />} />
          <Route path="/contactus" element={<ContactUsPage />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/memberss" element={<PublicMembersPage />} />
          <Route path="/programs" element={<ProgramsPage />} />
          <Route path="/disability-policies-research" element={<DisabilityPoliciesResearchPage />} />
          <Route path="/news" element={<NewsPage />} />
          <Route path="/news/:id" element={<NewsDetail />} />
          <Route path="/login" element={
            <Login />
            } />
          <Route path="/forgot-password" element={<ForgotPassword />} />

          {/* OPD Routes */}
          <Route
            path="/dashboard"
            element={
              <OPDProtectedRoute>
              <Layout nav={OpdsNav}>
                <OPDDashboard />
              </Layout>
              </OPDProtectedRoute>
            }
          />
          <Route
            path="/members"
            element={
              <OPDProtectedRoute>
              <Layout nav={OpdsNav}>
                <MembersPage />
              </Layout>
              </OPDProtectedRoute>
            }
          />
          <Route
            path="/members/pending"
            element={
              <OPDProtectedRoute>
              <Layout nav={OpdsNav}>
                <OPDPendingMembers />
              </Layout>
              </OPDProtectedRoute>
            }
          />
          <Route
            path="/members/rejected"
            element={
              <OPDProtectedRoute>
              <Layout nav={OpdsNav}>
                <OPDRejectedMembers />
              </Layout>
              </OPDProtectedRoute>
            }
          />
          <Route
            path="/members/renewals"
            element={
              <OPDProtectedRoute>
              <Layout nav={OpdsNav}>
                <OPDRenewals />
              </Layout>
              </OPDProtectedRoute>
            }
          />
          <Route
            path="/chats"
            element={
              <OPDProtectedRoute>
              <Layout nav={OpdsNav}>
                <ChatsPage />
              </Layout>
              </OPDProtectedRoute>
            }
          />
          <Route
            path="/opdsupport"
            element={
              <OPDProtectedRoute>
              <Layout nav={OpdsNav}>
                <OPDSupportReceived />
              </Layout>
              </OPDProtectedRoute>
            }
          />
           <Route
            path="/opdsupporttracking"
            element={
              <OPDProtectedRoute>
              <Layout nav={OpdsNav}>
                <OPDSupportTrackingPage />
              </Layout>
              </OPDProtectedRoute>
            }
          />
          <Route
            path="/self-registered-members"
            element={
              <OPDProtectedRoute>
              <Layout nav={OpdsNav}>
                <SelfRegisteredMembers />
              </Layout>
              </OPDProtectedRoute>
            }
          />
          <Route
            path="/opdreports"
            element={
              <OPDProtectedRoute>
              <Layout nav={OpdsNav}>
                <OPDReports />
              </Layout>
              </OPDProtectedRoute>
            }
          />
          <Route
            path="/opdplansreports"
            element={
              <OPDProtectedRoute>
              <Layout nav={OpdsNav}>
                <OPDPlansReports />
              </Layout>
              </OPDProtectedRoute>
            }
          />
          <Route
            path="/opdprofile"
            element={
              <OPDProtectedRoute>
              <Layout nav={OpdsNav}>
                <OPDProfile />
              </Layout>
              </OPDProtectedRoute>
            }
          />
          <Route
            path="/opdemailupdate"
            element={
              <OPDProtectedRoute>
              <Layout nav={OpdsNav}>
                <OPDEmailUpdate />
              </Layout>
              </OPDProtectedRoute>
            }
          />
          <Route
            path="/opdpasswordupdate"
            element={
              <OPDProtectedRoute>
              <Layout nav={OpdsNav}>
                <OPDPasswordUpdate />
              </Layout>
              </OPDProtectedRoute>
            }
          />
          <Route
            path="/opdaccessibility"
            element={
              <OPDProtectedRoute>
              <Layout nav={OpdsNav}>
                <OPDAccessibility />
              </Layout>
              </OPDProtectedRoute>
            }
          />
          <Route
            path="/deleted-members"
            element={
              <OPDProtectedRoute>
              <Layout nav={OpdsNav}>
                <DeletedMembersPage />
              </Layout>
              </OPDProtectedRoute>
            }
          />
          <Route
            path="/submit-gallery"
            element={
              <OPDProtectedRoute>
              <Layout nav={OpdsNav}>
                <SubmitGallery />
              </Layout>
              </OPDProtectedRoute>
            }
          />
          <Route
            path="/submit-news"
            element={
              <OPDProtectedRoute>
              <Layout nav={OpdsNav}>
                <SubmitNews />
              </Layout>
              </OPDProtectedRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admindashboard"
            element={
              <AdminProtectedRoute>
              <Layout nav={AdminNav}>
                <AssociationDashboard />
              </Layout>
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/adminmembers"
            element={
              <AdminProtectedRoute>
              <Layout nav={AdminNav}>
                <AssociationMembers />
              </Layout>
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/adminmembers/pending"
            element={
              <AdminProtectedRoute>
              <Layout nav={AdminNav}>
                <AssociationPendingMembers />
              </Layout>
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/adminmembers/rejected"
            element={
              <AdminProtectedRoute>
              <Layout nav={AdminNav}>
                <AssociationRejectedMembers />
              </Layout>
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/adminmembers/renewals"
            element={
              <AdminProtectedRoute>
              <Layout nav={AdminNav}>
                <AssociationRenewals />
              </Layout>
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/adminopds"
            element={
              <AdminProtectedRoute>
              <Layout nav={AdminNav}>
                <AdminOPDs />
              </Layout>
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/adminplansreports"
            element={
              <AdminProtectedRoute>
              <Layout nav={AdminNav}>
                <AdminPlansReports />
              </Layout>
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <AdminProtectedRoute>
              <Layout nav={AdminNav}>
                <AdminReports />
              </Layout>
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/galleryadmin"
            element={
              <AdminProtectedRoute>
              <Layout nav={AdminNav}>
                <GalleryAdmin />
              </Layout>
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/adminnews"
            element={
              <AdminProtectedRoute>
              <Layout nav={AdminNav}>
                <AdminNews />
              </Layout>
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/adminprograms"
            element={
              <AdminProtectedRoute>
              <Layout nav={AdminNav}>
                <ProgramsAdminPage />
              </Layout>
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/adminsettings"
            element={
              <AdminProtectedRoute>
              <Layout nav={AdminNav}>
                <AdminSettingsForm />
              </Layout>
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/associationemailupdate"
            element={
              <AdminProtectedRoute>
              <Layout nav={AdminNav}>
                <AssociationEmailUpdate />
              </Layout>
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/adminpasswordupdate"
            element={
              <AdminProtectedRoute>
              <Layout nav={AdminNav}>
                <AssociationPasswordUpdate />
              </Layout>
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/adminmessages"
            element={
              <AdminProtectedRoute>
              <Layout nav={AdminNav}>
                <AdminMessages />
              </Layout>
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/supportperopd"
            element={
              <AdminProtectedRoute>
              <Layout nav={AdminNav}>
                <PerOPDSupportTrackingPage />
              </Layout>
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/adminapprovalgallery"
            element={
              <AdminProtectedRoute>
              <Layout nav={AdminNav}>
                <AdminApprovalGallery />
              </Layout>
              </AdminProtectedRoute>
            }
          />
          <Route
            path="/adminapprovalnews"
            element={
              <AdminProtectedRoute>
              <Layout nav={AdminNav}>
                <AdminApprovalNews />
              </Layout>
              </AdminProtectedRoute>
            }
          />

          {/* Redirect unknown routes */}
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
        

        {/* Conditional Accessibility Controls - Show only on non-OPD and non-Admin pages */}
        {(!isOPDPage() && !isAdminPage()) && <FloatingAccessibilityControls />}

        {/* Add Toaster for notifications */}
        <Toaster
          position="top-center"
          toastOptions={{
            duration: 4000,
            style: {
              borderRadius: '8px',
              padding: '12px 16px',
              fontSize: '14px',
              maxWidth: '500px',
            },
            success: {
              style: {
                background: '#D1FAE5',
                color: '#047857',
              },
              iconTheme: {
                primary: '#10B981',
                secondary: '#ECFDF5',
              },
            },
            error: {
              style: {
                background: '#FEE2E2',
                color: '#B91C1C',
              },
              iconTheme: {
                primary: '#EF4444',
                secondary: '#FEF2F2',
              },
            },
          }}
        />
      </div>
    </VoiceCommandProvider>
  );
};

function App() {
  return (
    <NotificationProvider>
      <AccessibilityProvider>
        <LanguageProvider>
          <Router>
            <ScrollToTop />
            <ConditionalFloatingForm />
            <AppWithVoice />
          </Router>
        </LanguageProvider>
      </AccessibilityProvider>
    </NotificationProvider>
  );
}

export default App;
